import { appName } from '../../context';

type Env = 'Dev' | 'QA' | 'REG' | 'UAT' | 'Prod';

/**
 * Common entity IDs shared across all environments and brands
 * These values serve as the base configuration
 */
const COMMON_ENTITY_IDS: Record<string, string> = {
  payMonthlyPremiumId: 'SentaraWHPSMemberportal',
  smartShopperId: 'urn%3Asp%3Aavmeduat%3Asmartshopper',
  rxSavingsSolutionsId:
    'https%3A%2F%2Fapi-stage.rxsavingssolutions.com%2Fsso%2Fsentara%2Fsaml%2Fmetadata',
  hingeHealthId: 'urn%3Aauth0%3Ahinge-health-stage%3Asentara',
  behavioralHealthId: 'uhg.stage.lww',
  calculateTreatmentCostId: 'uat.thconsumeradvantage.com%3Aavmed-mbr.org.stage',
};

/**
 * Brand-specific entity ID configurations
 * Each brand can override common values or add brand-specific ones
 */
const BRAND_SPECIFIC_ENTITY_IDS: Record<string, Record<string, string>> = {
  SHP: {
    ...COMMON_ENTITY_IDS,
    // Sentara-specific overrides or additions
    healthSavingAccountsId: 'HealthEquity',
    healthReimbursementAccountId: 'HealthEquity',
    DentalDominionId: 'https%3A%2F%2Fddsso.dominionnational.com%2FAuthServices',
    viewCarePlanId: 'https%3A%2F%2Fshpuatrmp.zeomega.com',
    vlocityEnrollBenefitsId: 'SHPSalesforceVlocitySSO',
    manageRenewPolicyId: 'Siebel',
  },
  AvMed: {
    ...COMMON_ENTITY_IDS,
    // AvMed-specific overrides or additions
    manageRenewPolicyId: 'Avmed.org%3Asaml2.0',
  },
};

/**
 * Environment-specific overrides per brand
 * Structure: brand -> environment -> entity overrides
 */
const ENVIRONMENT_SPECIFIC_OVERRIDES: Record<
  string,
  Partial<Record<Env, Record<string, string>>>
> = {
  SHP: {
    Prod: {
      // Production environment overrides/new-properties for Sentara
      hingeHealthId: 'urn%3Aauth0%3Ahinge-health%3Asentara',
      DentalDominionId:
        'https%3A%2F%2Fmembersso.dominionnational.com%2FAuthServices',
      viewCarePlanId: 'https%3A%2F%2Fshprmp.zeomega.com',
    },
    // Add other environment overrides for Sentara
  },
  AvMed: {
    Prod: {
      // Production environment overrides/new-properties for AvMed
      hingeHealthId: 'urn%3Aauth0%3Ahinge-health%3Asentara', // This can be changed as needed,
      behavioralHealthId: 'uhg.prod.lww',
      calculateTreatmentCostId:
        'www.thconsumeradvantage.com%3Aavmed-mbr.org.prod',
      rxSavingsSolutionsId:
        'https%3A%2F%2Fapi.rxsavingssolutions.com%2Fsso%2Fsentara%2Fsaml%2Fmetadata',
      smartShopperId: 'urn%3Asp%3Aavmed%3Asmartshopper',
      payMonthlyPremiumId: 'SentaraWHPSPRODMemberportal',
      manageRenewPolicyId: 'Avmed.org%3Asaml2.0',
    },
    // Add other environment overrides for AvMed
  },
};

/**
 * Get brand-specific entity IDs with environment overrides
 */
const getBrandEntityIds = (
  brand: string,
  environment?: Env
): Record<string, string> => {
  const baseIds = BRAND_SPECIFIC_ENTITY_IDS[brand] || COMMON_ENTITY_IDS;

  if (environment && ENVIRONMENT_SPECIFIC_OVERRIDES[brand]?.[environment]) {
    return {
      ...baseIds,
      ...ENVIRONMENT_SPECIFIC_OVERRIDES[brand][environment],
    };
  }

  return baseIds;
};

/**
 * Service Provider Entity IDs configuration for different environments
 * Now considers both BRAND and environment-specific overrides
 */
const SP_ENTITY_IDS: Record<Env, Record<string, string>> = {
  QA: getBrandEntityIds(appName, 'QA'),
  UAT: getBrandEntityIds(appName, 'UAT'),
  Dev: getBrandEntityIds(appName, 'Dev'),
  Prod: getBrandEntityIds(appName, 'Prod'),
  REG: getBrandEntityIds(appName, 'REG'),
};

export default SP_ENTITY_IDS;
export { getBrandEntityIds };
